import React, { Suspense } from 'react';
import dynamic from 'next/dynamic';
import { ArticleConstants, ArticleHeroBlockConstants } from '../../adapters/helpers/Constants';
import Block from '../../adapters/helpers/Block';
import { Label } from '../Label/Label';
import { mediaQueryHOC } from '../../adapters/helpers/Hooks';
import DOMPurify from 'isomorphic-dompurify';

const Image = dynamic(() => import('../Image/Image'));

interface ArticleOverviewFields {
    showImage?: boolean;
    thumbnailImage?: object;
    title?: string;
    description?: string;
    readTime?: string;
}

interface CategoryFields {
    title?: string;
    slug?: {
        fields?: {
            slug?: string;
        };
    };
}

interface AssetFileFields {
    url?: string;
}

interface AssetFields {
    file?: AssetFileFields;
}

interface BackgroundAsset {
    fields?: {
        asset?: {
            fields?: AssetFields;
        };
    };
}

interface ExtraAttributes {
    entity?: {
        articleOverview?: {
            fields?: ArticleOverviewFields;
        };
        category?: {
            fields?: CategoryFields;
        };
        backgroundAsset?: BackgroundAsset;
        name?: string;
    };
}

interface LegacyArticleHeroBlockProps {
    extraAttributes?: ExtraAttributes;
    isMedium?: boolean;
    isSmall?: boolean;
}

const LegacyArticleHeroBlock: React.FC<LegacyArticleHeroBlockProps> = (props) => {
    const block = new Block(props);
    const { extraAttributes, isSmall } = props;

    const className = block.getFieldValue('classNames');
    const anchorId = block.getAnchorId();
    const showImage = extraAttributes?.entity?.articleOverview?.fields?.showImage;
    const thumbnailImage = extraAttributes?.entity?.articleOverview?.fields?.thumbnailImage;
    const mobileImage = block.getFieldValue('mobileBackgroundAsset');
    const title = extraAttributes?.entity?.articleOverview?.fields?.title;
    const eyebrow = extraAttributes?.entity?.category?.fields?.title;
    const slug = extraAttributes?.entity?.category?.fields?.slug?.fields?.slug;
    const readingTimeLabel = Label({
        label: block.getFieldValue(ArticleHeroBlockConstants.readTimeLabel),
        tokens: { readTime: extraAttributes?.entity?.articleOverview?.fields?.readTime },
    });
    const body = extraAttributes?.entity?.articleOverview?.fields?.description;
    const backgroundAsset = extraAttributes?.entity?.backgroundAsset;
    const changeSequence = extraAttributes?.entity?.name === ArticleConstants.GetHelpPage;

    const renderImage = () => {
        return (
            <div className="my-class w-full h-full mt-0">
                <Image
                    className={
                        isSmall
                            ? 'ob-mini-content-mobile-image min-h-20'
                            : 'ob-mini-content-dektop-image min-h-37 w-full'
                    }
                    image={backgroundAsset}
                />
            </div>
        );
    };

    const renderText = () => {
        return (
            <div className="bg-revamp_bg flex flex-col items-start justify-center p-3 pr-8 lg:py-0 lg:px-5 2lg:px-9 mt-0">
                {eyebrow && !changeSequence && (
                    <p
                        className="font-semi-bold text-revamp_white text-base lg:text-xl leading-tight tracking-normal mb-4"
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(eyebrow) }}
                    />
                )}
                {title && (
                    <h1
                        className="font-light text-revamp_white text-28 lg:text-4xl xl:text-5xl leading-tight tracking-normal mt-0"
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(title) }}
                    />
                )}
                {eyebrow && changeSequence && (
                    <p
                        className="font-semi-bold text-revamp_white text-base lg:text-xl leading-tight tracking-normal mb-4 mt-4"
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(eyebrow) }}
                    />
                )}
            </div>
        );
    };

    return (
        <div className={`Article-hero-banner flex flex-col md:grid md:grid-cols-2 ${className || ''}`} id={anchorId}>
            {renderImage()}
            {renderText()}
        </div>
    );
};

export default mediaQueryHOC(LegacyArticleHeroBlock);